import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  useToast,
  Flex,
  IconButton,
  Tooltip,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  SearchIcon,
  EditIcon,
  CheckIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import {
  getProspectListById,
  getProspectListStatistics,
  updateProspectPlace,
} from "../services/api";
import ProspectPlaceCard from "../components/ProspectPlaceCard";

const ProspeccaoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [list, setList] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listResult, statsResult] = await Promise.all([
        getProspectListById(id),
        getProspectListStatistics(id),
      ]);

      if (listResult) {
        setList(listResult);
      } else {
        toast({
          title: "Lista não encontrada",
          description: "A lista de prospecção não foi encontrada",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/prospeccao");
      }

      if (statsResult) {
        setStatistics(statsResult);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar dados da lista",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceUpdate = async (placeId, updateData) => {
    try {
      const result = await updateProspectPlace(id, placeId, updateData);
      if (result && !result.error) {
        toast({
          title: "Atualizado",
          description: "Lugar atualizado com sucesso",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        loadData(); // Recarregar dados
      } else {
        throw new Error(result?.error || "Erro ao atualizar lugar");
      }
    } catch (error) {
      console.error("Erro ao atualizar place:", error);
      toast({
        title: "Erro",
        description: typeof error === 'string' ? error : error.message || "Erro ao atualizar lugar",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      throw error; // Re-throw para que o modal saiba que deu erro
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // Filtrar lugares
  const filterPlaces = (places) => {
    if (!places) return [];

    return places.filter((place) => {
      const matchesSearch = searchTerm === "" ||
        place.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (place.address && place.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (place.contact_person_name && place.contact_person_name.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === "all" || place.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  // Organizar lugares por status
  const organizePlacesByStatus = (places) => {
    const filtered = filterPlaces(places);
    return {
      pending: filtered.filter(p => p.status === "pending"),
      contacted: filtered.filter(p => p.status === "contacted"),
      in_negotiation: filtered.filter(p => p.status === "in_negotiation"),
      converted: filtered.filter(p => p.status === "converted"),
      lost: filtered.filter(p => p.status === "lost"),
    };
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="center">
          <Spinner size="xl" />
          <Text>Carregando detalhes da lista...</Text>
        </VStack>
      </Container>
    );
  }

  if (!list) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="center">
          <Text>Lista não encontrada</Text>
          <Button onClick={() => navigate("/prospeccao")}>
            Voltar para Prospecção
          </Button>
        </VStack>
      </Container>
    );
  }

  const organizedPlaces = organizePlacesByStatus(list.places);

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              onClick={() => navigate("/prospeccao")}
              aria-label="Voltar"
            />
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="gray.700">
                {list.name}
              </Heading>
              <Text color="gray.600" fontSize="md">
                "{list.search_query}"
              </Text>
            </VStack>
          </HStack>

          <Badge
            colorScheme={list.is_completed ? "green" : "orange"}
            px={3}
            py={1}
            borderRadius="full"
          >
            {list.is_completed ? (
              <>
                <CheckIcon mr={1} />
                Completa
              </>
            ) : (
              <>
                <TimeIcon mr={1} />
                Em andamento
              </>
            )}
          </Badge>
        </Flex>

        {/* Estatísticas */}
        {statistics && (
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }} gap={4}>
            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Total</StatLabel>
                    <StatNumber color="blue.500">{statistics.total_places}</StatNumber>
                    <StatHelpText>lugares</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Contactados</StatLabel>
                    <StatNumber color="green.500">{statistics.contacted_places}</StatNumber>
                    <StatHelpText>
                      {statistics.total_places > 0
                        ? Math.round((statistics.contacted_places / statistics.total_places) * 100)
                        : 0}%
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Em Negociação</StatLabel>
                    <StatNumber color="yellow.500">{statistics.in_negotiation}</StatNumber>
                    <StatHelpText>ativos</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Convertidos</StatLabel>
                    <StatNumber color="teal.500">{statistics.converted_places}</StatNumber>
                    <StatHelpText>
                      {statistics.conversion_rate && typeof statistics.conversion_rate === 'number' ? statistics.conversion_rate.toFixed(1) : "0,0"}% taxa
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Valor Total</StatLabel>
                    <StatNumber color="green.500" fontSize="md">
                      R$ {statistics.total_conversion_value && typeof statistics.total_conversion_value === 'number' ? statistics.total_conversion_value.toFixed(2) : "0,00"}
                    </StatNumber>
                    <StatHelpText>convertido</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card>
                <CardBody>
                  <Stat size="sm">
                    <StatLabel>Retornos</StatLabel>
                    <StatNumber color="orange.500">{statistics.places_need_return}</StatNumber>
                    <StatHelpText>pendentes</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        )}

        {/* Progress Bar */}
        <Card>
          <CardBody>
            <VStack spacing={3}>
              <HStack justify="space-between" width="100%">
                <Text fontWeight="semibold">Progresso Geral</Text>
                <Text fontWeight="semibold" color="blue.500">
                  {statistics
                    ? Math.round((statistics.contacted_places / statistics.total_places) * 100)
                    : 0}%
                </Text>
              </HStack>
              <Progress
                value={statistics
                  ? (statistics.contacted_places / statistics.total_places) * 100
                  : 0}
                colorScheme="blue"
                size="lg"
                borderRadius="full"
                width="100%"
              />
              <Text fontSize="sm" color="gray.600">
                {statistics?.contacted_places || 0} de {statistics?.total_places || 0} lugares contactados
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {/* Filtros */}
        <Card>
          <CardBody>
            <HStack spacing={4}>
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar lugares..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                <option value="pending">Pendentes</option>
                <option value="contacted">Contactados</option>
                <option value="in_negotiation">Em Negociação</option>
                <option value="converted">Convertidos</option>
                <option value="lost">Perdidos</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Lugares organizados por status */}
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
          <TabList>
            <Tab>
              Pendentes ({organizedPlaces.pending.length})
            </Tab>
            <Tab>
              Contactados ({organizedPlaces.contacted.length})
            </Tab>
            <Tab>
              Em Negociação ({organizedPlaces.in_negotiation.length})
            </Tab>
            <Tab>
              Convertidos ({organizedPlaces.converted.length})
            </Tab>
            <Tab>
              Perdidos ({organizedPlaces.lost.length})
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <PlacesGrid places={organizedPlaces.pending} onUpdate={handlePlaceUpdate} listId={id} />
            </TabPanel>
            <TabPanel>
              <PlacesGrid places={organizedPlaces.contacted} onUpdate={handlePlaceUpdate} listId={id} />
            </TabPanel>
            <TabPanel>
              <PlacesGrid places={organizedPlaces.in_negotiation} onUpdate={handlePlaceUpdate} listId={id} />
            </TabPanel>
            <TabPanel>
              <PlacesGrid places={organizedPlaces.converted} onUpdate={handlePlaceUpdate} listId={id} />
            </TabPanel>
            <TabPanel>
              <PlacesGrid places={organizedPlaces.lost} onUpdate={handlePlaceUpdate} listId={id} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

const PlacesGrid = ({ places, onUpdate, listId }) => {
  if (places.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color="gray.500" fontSize="lg">
          Nenhum lugar encontrado nesta categoria
        </Text>
      </Box>
    );
  }

  return (
    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={6}>
      {places.map((place) => (
        <ProspectPlaceCard
          key={place.id}
          place={place}
          listId={listId}
          onUpdate={onUpdate}
        />
      ))}
    </Grid>
  );
};

export default ProspeccaoDetalhes;